import { useState, useEffect } from 'react';
import { Calendar, Check, X, MapPin, Users, Clock, CalendarPlus, HelpCircle } from 'lucide-react';
import cloudflareApi from '../lib/cloudflare-api';
import { Card } from './ui';
import Button from './ui/Button';

interface CalendarInvitationCardProps {
  emailId: string;
  emailBody?: string;
  onAccept?: () => void;
  onDecline?: () => void;
}

interface ParsedEvent {
  title: string;
  startTime: Date | null;
  endTime: Date | null;
  location: string;
  attendees: string[];
}

function parseIcsDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  try {
    const cleanDate = dateStr.replace(/^TZID=[^:]+:/, '');
    if (cleanDate.length < 8) return null;

    const year = parseInt(cleanDate.substring(0, 4));
    const month = parseInt(cleanDate.substring(4, 6)) - 1;
    const day = parseInt(cleanDate.substring(6, 8));
    const hour = cleanDate.length >= 13 ? parseInt(cleanDate.substring(9, 11)) : 0;
    const minute = cleanDate.length >= 15 ? parseInt(cleanDate.substring(11, 13)) : 0;

    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

    if (cleanDate.endsWith('Z')) {
      return new Date(Date.UTC(year, month, day, hour, minute));
    }
    return new Date(year, month, day, hour, minute);
  } catch {
    return null;
  }
}

function extractIcsFromBody(body: string | undefined | null): string | null {
  if (!body || typeof body !== 'string') return null;

  try {
    const vcalendarMatch = body.match(/BEGIN:VCALENDAR[\s\S]*?END:VCALENDAR/i);
    if (vcalendarMatch) {
      return vcalendarMatch[0];
    }
    return null;
  } catch {
    return null;
  }
}

function parseIcsPreview(ics: string): ParsedEvent | null {
  if (!ics) return null;

  try {
    const getIcsValue = (key: string): string => {
      try {
        const regex = new RegExp(`^${key}[;:](.*)$`, 'mi');
        const match = ics.match(regex);
        if (!match) return '';
        let value = match[1] || '';
        const lines = ics.split(/\r?\n/);
        const keyIndex = lines.findIndex(l => l.match(regex));
        if (keyIndex >= 0) {
          for (let i = keyIndex + 1; i < lines.length; i++) {
            if (lines[i]?.startsWith(' ') || lines[i]?.startsWith('\t')) {
              value += lines[i].substring(1);
            } else {
              break;
            }
          }
        }
        return value.trim();
      } catch {
        return '';
      }
    };

    const summary = getIcsValue('SUMMARY') || 'Sin titulo';
    const location = getIcsValue('LOCATION')
      .replace(/\\,/g, ',')
      .replace(/\\\\/g, '\\');
    const dtstart = getIcsValue('DTSTART');
    const dtend = getIcsValue('DTEND');

    const attendeesRaw = ics.match(/^ATTENDEE[;:].+$/gmi) || [];
    const attendees = attendeesRaw
      .slice(0, 50)
      .map(a => {
        const emailMatch = a.match(/mailto:([^\s;]+)/i);
        return emailMatch ? emailMatch[1] : '';
      }).filter(Boolean);

    return {
      title: summary,
      startTime: parseIcsDate(dtstart),
      endTime: parseIcsDate(dtend),
      location,
      attendees
    };
  } catch {
    console.error('Error parsing ICS preview');
    return null;
  }
}

export default function CalendarInvitationCard({ emailId, emailBody, onAccept, onDecline }: CalendarInvitationCardProps) {
  const [loading, setLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [icsContent, setIcsContent] = useState<string | null>(null);
  const [eventPreview, setEventPreview] = useState<ParsedEvent | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      if (emailBody) {
        const ics = extractIcsFromBody(emailBody);
        setIcsContent(ics);
        if (ics) {
          setEventPreview(parseIcsPreview(ics));
        }
      }
    } catch (err) {
      console.error('Error processing calendar invitation:', err);
      setIcsContent(null);
      setEventPreview(null);
    }
  }, [emailBody]);

  const handleAccept = async () => {
    if (!icsContent) {
      setError('No se pudo encontrar la informacion del evento en el correo.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await cloudflareApi.createEventFromIcs(icsContent, emailId);
      console.log('Evento creado:', result);
      setAccepted(true);
      if (onAccept) onAccept();
    } catch (err) {
      console.error('Error aceptando invitacion:', err);
      setError(`Error al crear el evento: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      setDeclined(true);
      if (onDecline) onDecline();
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (accepted) {
    return (
      <Card variant="glass" padding="lg" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-lime-500/8 via-transparent to-emerald-500/5 pointer-events-none" />
        <div className="relative flex items-center gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-lime-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-lime-500/25">
            <Check size={22} className="text-white" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              Invitacion aceptada
            </h3>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
              El evento se ha agregado a tu calendario
            </p>
          </div>
          <div className="flex-shrink-0">
            <CalendarPlus size={18} className="text-lime-500/40" />
          </div>
        </div>
      </Card>
    );
  }

  if (declined) {
    return (
      <Card variant="glass" padding="lg" className="relative overflow-hidden opacity-75">
        <div className="relative flex items-center gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-[var(--bg-hover)] flex items-center justify-center border border-[var(--border)]">
            <X size={20} className="text-[var(--text-tertiary)]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[var(--text-secondary)]">
              Invitacion rechazada
            </h3>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
              Has rechazado esta invitacion
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="glass" padding="none" className="relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-lime-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center gap-3 px-5 pt-5 pb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-lime-500/15 to-lime-400/8 border border-lime-500/15 flex items-center justify-center">
          <Calendar size={18} className="text-lime-600 dark:text-lime-400" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-[var(--text-primary)]">
            Invitacion de Calendario
          </span>
          <span className="block text-[10px] font-medium text-lime-600/70 dark:text-lime-400/60 uppercase tracking-wider mt-0.5">
            Evento pendiente
          </span>
        </div>
      </div>

      <div className="mx-5 h-px bg-gradient-to-r from-[var(--border)] via-lime-500/10 to-[var(--border)]" />

      {/* Body */}
      <div className="px-5 py-4">
        {eventPreview ? (
          <div className="space-y-1">
            <h4 className="text-base font-semibold text-[var(--text-primary)] leading-snug mb-3">
              {eventPreview.title}
            </h4>

            {eventPreview.startTime && (
              <div className="flex items-center gap-3 py-2 px-2.5 -mx-2.5 rounded-lg" style={{ transition: 'background-color 150ms ease' }}>
                <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-lime-500/10 flex items-center justify-center">
                  <Clock size={14} className="text-lime-600 dark:text-lime-400" />
                </div>
                <span className="text-sm text-[var(--text-secondary)] leading-snug">
                  {formatDateTime(eventPreview.startTime)}
                  {eventPreview.endTime && (
                    <span className="text-[var(--text-tertiary)]">
                      {' '}— {eventPreview.endTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </span>
              </div>
            )}

            {eventPreview.location && (
              <div className="flex items-center gap-3 py-2 px-2.5 -mx-2.5 rounded-lg" style={{ transition: 'background-color 150ms ease' }}>
                <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-lime-500/10 flex items-center justify-center">
                  <MapPin size={14} className="text-lime-600 dark:text-lime-400" />
                </div>
                <span className="text-sm text-[var(--text-secondary)] truncate">
                  {eventPreview.location}
                </span>
              </div>
            )}

            {eventPreview.attendees.length > 0 && (
              <div className="flex items-center gap-3 py-2 px-2.5 -mx-2.5 rounded-lg" style={{ transition: 'background-color 150ms ease' }}>
                <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-lime-500/10 flex items-center justify-center">
                  <Users size={14} className="text-lime-600 dark:text-lime-400" />
                </div>
                <span className="text-sm text-[var(--text-secondary)]">
                  {eventPreview.attendees.length} invitado{eventPreview.attendees.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-[var(--text-tertiary)]">
            Este correo contiene una invitacion de calendario.
          </p>
        )}

        {error && (
          <div className="mt-3 px-3 py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-600 dark:text-rose-400 leading-relaxed">
            {error}
          </div>
        )}
      </div>

      <div className="mx-5 h-px bg-[var(--border)]" />

      {/* Actions */}
      <div className="px-5 py-4 flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          loading={loading}
          icon={<Check size={15} />}
          onClick={handleAccept}
          disabled={loading}
          className="flex-1"
        >
          Aceptar
        </Button>

        <Button
          variant="secondary"
          size="sm"
          icon={<HelpCircle size={15} />}
          onClick={() => {}}
          disabled={loading}
          className="flex-1"
        >
          Tentativo
        </Button>

        <Button
          variant="ghost"
          size="sm"
          icon={<X size={15} />}
          onClick={handleDecline}
          disabled={loading}
          className="flex-1"
        >
          Rechazar
        </Button>
      </div>
    </Card>
  );
}
